/**
 * Roles management component for admin panel with full CRUD operations.
 */

import { useState, useEffect } from 'react';
import { Button, Table, Form, Modal, Alert, Spinner, Badge, ListGroup } from 'react-bootstrap';
import { apiClient } from '@/services/apiClient';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

interface Role {
  id: number;
  name: string;
  description?: string;
}

interface RoleDetail {
  role: Role;
  devices: Array<{ id: number; device_id: string; description?: string }>;
  clinics: Array<{ id: number; clinic_id: string; name: string; description?: string }>;
  studies: Array<{ id: number; study_id: string; name: string; description?: string }>;
  data_types: Array<{ id: number; name: string; description?: string }>;
  accessible_datasets_count: number;
}

interface Entity {
  id: number;
  name?: string;
  device_id?: string;
  clinic_id?: string;
  study_id?: string;
  description?: string;
}

export default function RolesManagement() {
  const { t } = useTranslation();
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [selectedRole, setSelectedRole] = useState<RoleDetail | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [allDevices, setAllDevices] = useState<Entity[]>([]);
  const [allClinics, setAllClinics] = useState<Entity[]>([]);
  const [allStudies, setAllStudies] = useState<Entity[]>([]);
  const [allDataTypes, setAllDataTypes] = useState<Entity[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });

  useEffect(() => {
    loadRoles();
    loadAllEntities();
  }, []);

  const loadAllEntities = async () => {
    try {
      const [devicesRes, clinicsRes, studiesRes, dataTypesRes] = await Promise.all([
        apiClient.getAdminDevices(),
        apiClient.getAdminClinics(),
        apiClient.getAdminStudies(),
        apiClient.getAdminDataTypes(),
      ]);

      if (!devicesRes.error && devicesRes.data) setAllDevices(devicesRes.data as Entity[]);
      if (!clinicsRes.error && clinicsRes.data) setAllClinics(clinicsRes.data as Entity[]);
      if (!studiesRes.error && studiesRes.data) setAllStudies(studiesRes.data as Entity[]);
      if (!dataTypesRes.error && dataTypesRes.data) setAllDataTypes(dataTypesRes.data as Entity[]);
    } catch (error) {
      console.error('Failed to load entities:', error);
    }
  };

  const loadRoles = async () => {
    setLoading(true);
    try {
      const response = await apiClient.getAdminRoles();

      if (response.error) {
        toast.error(response.error);
      } else {
        setRoles((response.data as Role[]) || []);
      }
    } catch (error) {
      toast.error('Failed to load roles');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingRole(null);
    setFormData({
      name: '',
      description: '',
    });
    setShowModal(true);
  };

  const handleEdit = (role: Role) => {
    setEditingRole(role);
    setFormData({
      name: role.name,
      description: role.description || '',
    });
    setShowModal(true);
  };

  const handleDelete = async (roleId: number) => {
    const role = roles.find((r) => r.id === roleId);
    const roleName = role?.name || 'this role';

    if (!window.confirm(`Are you sure you want to delete "${roleName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await apiClient.deleteAdminRole(roleId);
      if (response.error) {
        toast.error(response.error);
      } else {
        toast.success('Role deleted successfully');
        loadRoles();
      }
    } catch (error) {
      toast.error('Failed to delete role');
      console.error(error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingRole) {
        const response = await apiClient.updateAdminRole(editingRole.id, {
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
        });

        if (response.error) {
          toast.error(response.error);
        } else {
          toast.success('Role updated successfully');
          setShowModal(false);
          loadRoles();
        }
      } else {
        const response = await apiClient.createAdminRole({
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
        });

        if (response.error) {
          toast.error(response.error);
        } else {
          toast.success('Role created successfully');
          setShowModal(false);
          loadRoles();
        }
      }
    } catch (error) {
      toast.error('Failed to save role');
      console.error(error);
    }
  };

  const handleViewDetails = async (role: Role) => {
    setLoadingDetails(true);
    setShowDetailsModal(true);
    try {
      const response = await apiClient.getAdminRoleDetail(role.id);
      if (response.error) {
        toast.error(response.error);
        setShowDetailsModal(false);
      } else {
        setSelectedRole(response.data as RoleDetail);
      }
    } catch (error) {
      toast.error('Failed to load role details');
      console.error(error);
      setShowDetailsModal(false);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleToggleDevice = async (deviceId: number) => {
    if (!selectedRole) return;
    const hasDevice = selectedRole.devices.some((d) => d.id === deviceId);
    try {
      let response;
      if (hasDevice) {
        response = await apiClient.removeDeviceFromRole(selectedRole.role.id, deviceId);
      } else {
        response = await apiClient.addDeviceToRole(selectedRole.role.id, deviceId);
      }
      if (response.error) {
        toast.error(response.error);
      } else {
        toast.success(`Device ${hasDevice ? 'removed' : 'added'} successfully`);
        handleViewDetails(selectedRole.role);
      }
    } catch (error) {
      toast.error('Failed to update device association');
      console.error(error);
    }
  };

  const handleToggleClinic = async (clinicId: number) => {
    if (!selectedRole) return;
    const hasClinic = selectedRole.clinics.some((c) => c.id === clinicId);
    try {
      let response;
      if (hasClinic) {
        response = await apiClient.removeClinicFromRole(selectedRole.role.id, clinicId);
      } else {
        response = await apiClient.addClinicToRole(selectedRole.role.id, clinicId);
      }
      if (response.error) {
        toast.error(response.error);
      } else {
        toast.success(`Clinic ${hasClinic ? 'removed' : 'added'} successfully`);
        handleViewDetails(selectedRole.role);
      }
    } catch (error) {
      toast.error('Failed to update clinic association');
      console.error(error);
    }
  };

  const handleToggleStudy = async (studyId: number) => {
    if (!selectedRole) return;
    const hasStudy = selectedRole.studies.some((s) => s.id === studyId);
    try {
      let response;
      if (hasStudy) {
        response = await apiClient.removeStudyFromRole(selectedRole.role.id, studyId);
      } else {
        response = await apiClient.addStudyToRole(selectedRole.role.id, studyId);
      }
      if (response.error) {
        toast.error(response.error);
      } else {
        toast.success(`Study ${hasStudy ? 'removed' : 'added'} successfully`);
        handleViewDetails(selectedRole.role);
      }
    } catch (error) {
      toast.error('Failed to update study association');
      console.error(error);
    }
  };

  const handleToggleDataType = async (dataTypeId: number) => {
    if (!selectedRole) return;
    const hasDataType = selectedRole.data_types.some((dt) => dt.id === dataTypeId);
    try {
      let response;
      if (hasDataType) {
        response = await apiClient.removeDataTypeFromRole(selectedRole.role.id, dataTypeId);
      } else {
        response = await apiClient.addDataTypeToRole(selectedRole.role.id, dataTypeId);
      }
      if (response.error) {
        toast.error(response.error);
      } else {
        toast.success(`Data type ${hasDataType ? 'removed' : 'added'} successfully`);
        handleViewDetails(selectedRole.role);
      }
    } catch (error) {
      toast.error('Failed to update data type association');
      console.error(error);
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h2 className="mb-1">{t('admin.roles')}</h2>
          <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
        Roles define permissions for accessing datasets. Assign roles to user groups to grant permissions.
      </p>
        </div>
        <Button onClick={handleCreate} variant="primary">
          {t('common.create')} Role
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-4">
          <Spinner animation="border" />
        </div>
      ) : roles.length === 0 ? (
        <Alert variant="info">No roles found. Create your first role to get started.</Alert>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Description</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {roles.map((role) => (
              <tr key={role.id}>
                <td>{role.id}</td>
                <td>{role.name}</td>
                <td>{role.description || '-'}</td>
                <td>
                  <Button
                    size="sm"
                    variant="outline-info"
                    onClick={() => handleViewDetails(role)}
                    className="me-2"
                  >
                    {t('common.view')} Details
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-primary"
                    onClick={() => handleEdit(role)}
                    className="me-2"
                  >
                    {t('common.edit')}
                  </Button>
                  <Button size="sm" variant="outline-danger" onClick={() => handleDelete(role.id)}>
                    {t('common.delete')}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{editingRole ? 'Edit Role' : 'Create Role'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Name *</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter role name"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Enter role description (optional)"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" type="submit">
              {editingRole ? t('common.update') : `${t('common.create')} Role`}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Role Details: {selectedRole?.role.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingDetails ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          ) : selectedRole ? (
            <div>
              <Alert variant="info" className="mb-3">
                <strong>Accessible Datasets:</strong> {selectedRole.accessible_datasets_count}
                <br />
                <small>
                  This count represents datasets that match the role's associated devices, clinics, studies, or data
                  types.
                </small>
              </Alert>

              <h5>Devices</h5>
              <ListGroup className="mb-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {selectedRole.devices.length === 0 ? (
                  <ListGroup.Item>No devices associated</ListGroup.Item>
                ) : (
                  selectedRole.devices.map((device) => (
                    <ListGroup.Item key={device.id} className="d-flex justify-content-between align-items-center">
                      <span>
                        <Badge bg="primary" className="me-2">
                          {device.device_id}
                        </Badge>
                        {device.description}
                      </span>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => handleToggleDevice(device.id)}
                      >
                        Remove
                      </Button>
                    </ListGroup.Item>
                  ))
                )}
              </ListGroup>
              <Form.Select
                className="mb-4"
                onChange={(e) => {
                  if (e.target.value) {
                    handleToggleDevice(parseInt(e.target.value, 10));
                    e.target.value = '';
                  }
                }}
              >
                <option value="">Add device...</option>
                {allDevices
                  .filter((d) => !selectedRole.devices.some((sd) => sd.id === d.id))
                  .map((device) => (
                    <option key={device.id} value={device.id}>
                      {device.device_id} {device.description ? `- ${device.description}` : ''}
                    </option>
                  ))}
              </Form.Select>

              <h5>Clinics</h5>
              <ListGroup className="mb-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {selectedRole.clinics.length === 0 ? (
                  <ListGroup.Item>No clinics associated</ListGroup.Item>
                ) : (
                  selectedRole.clinics.map((clinic) => (
                    <ListGroup.Item key={clinic.id} className="d-flex justify-content-between align-items-center">
                      <span>
                        <Badge bg="success" className="me-2">
                          {clinic.name}
                        </Badge>
                        ({clinic.clinic_id}) {clinic.description}
                      </span>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => handleToggleClinic(clinic.id)}
                      >
                        Remove
                      </Button>
                    </ListGroup.Item>
                  ))
                )}
              </ListGroup>
              <Form.Select
                className="mb-4"
                onChange={(e) => {
                  if (e.target.value) {
                    handleToggleClinic(parseInt(e.target.value, 10));
                    e.target.value = '';
                  }
                }}
              >
                <option value="">Add clinic...</option>
                {allClinics
                  .filter((c) => !selectedRole.clinics.some((sc) => sc.id === c.id))
                  .map((clinic) => (
                    <option key={clinic.id} value={clinic.id}>
                      {clinic.name} ({clinic.clinic_id})
                    </option>
                  ))}
              </Form.Select>

              <h5>Studies</h5>
              <ListGroup className="mb-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {selectedRole.studies.length === 0 ? (
                  <ListGroup.Item>No studies associated</ListGroup.Item>
                ) : (
                  selectedRole.studies.map((study) => (
                    <ListGroup.Item key={study.id} className="d-flex justify-content-between align-items-center">
                      <span>
                        <Badge bg="warning" className="me-2 text-dark">
                          {study.name}
                        </Badge>
                        ({study.study_id}) {study.description}
                      </span>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => handleToggleStudy(study.id)}
                      >
                        Remove
                      </Button>
                    </ListGroup.Item>
                  ))
                )}
              </ListGroup>
              <Form.Select
                className="mb-4"
                onChange={(e) => {
                  if (e.target.value) {
                    handleToggleStudy(parseInt(e.target.value, 10));
                    e.target.value = '';
                  }
                }}
              >
                <option value="">Add study...</option>
                {allStudies
                  .filter((s) => !selectedRole.studies.some((ss) => ss.id === s.id))
                  .map((study) => (
                    <option key={study.id} value={study.id}>
                      {study.name} ({study.study_id})
                    </option>
                  ))}
              </Form.Select>

              <h5>Data Types</h5>
              <ListGroup className="mb-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {selectedRole.data_types.length === 0 ? (
                  <ListGroup.Item>No data types associated</ListGroup.Item>
                ) : (
                  selectedRole.data_types.map((dataType) => (
                    <ListGroup.Item
                      key={dataType.id}
                      className="d-flex justify-content-between align-items-center"
                    >
                      <span>
                        <Badge bg="info" className="me-2">
                          {dataType.name}
                        </Badge>
                        {dataType.description}
                      </span>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => handleToggleDataType(dataType.id)}
                      >
                        Remove
                      </Button>
                    </ListGroup.Item>
                  ))
                )}
              </ListGroup>
              <Form.Select
                className="mb-4"
                onChange={(e) => {
                  if (e.target.value) {
                    handleToggleDataType(parseInt(e.target.value, 10));
                    e.target.value = '';
                  }
                }}
              >
                <option value="">Add data type...</option>
                {allDataTypes
                  .filter((dt) => !selectedRole.data_types.some((sdt) => sdt.id === dt.id))
                  .map((dataType) => (
                    <option key={dataType.id} value={dataType.id}>
                      {dataType.name} {dataType.description ? `- ${dataType.description}` : ''}
                    </option>
                  ))}
              </Form.Select>
            </div>
          ) : (
            <Alert variant="danger">Failed to load role details</Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
            {t('common.close')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
